"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { api } from "@/lib/api";
import { useToast } from "@/components/toast";
import type { Notification } from "@/lib/types";

const POLL_MS = 60000;

const SEVERITY_DOT: Record<string, string> = {
  success: "bg-success-500",
  warning: "bg-warning-500",
  danger: "bg-danger-500",
  info: "bg-accent-500"
};

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return days < 7 ? `${days}d ago` : new Date(iso).toLocaleDateString();
}

// `align` says which way the panel opens FROM the bell:
//   "right" (default) -> panel's right edge meets the bell  (bell on the right, e.g. mobile header)
//   "left"            -> panel's left edge meets the bell   (bell in the narrow left sidebar, where
//                        opening leftward would run the 20rem panel straight off the screen)
export function NotificationBell({ align = "right" }: { align?: "left" | "right" }) {
  const router = useRouter();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Notification[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    try {
      const r = await api<{ notifications: Notification[]; unreadCount: number }>("account/notifications?limit=20");
      setItems(r.notifications || []);
      setUnread(r.unreadCount || 0);
    } catch {
      /* a failed poll is not worth bothering the customer about */
    }
  }, []);

  // Poll for the unread count; pause while the tab is hidden.
  useEffect(() => {
    void load();
    const timer = setInterval(() => {
      if (!document.hidden) void load();
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  async function markAllRead() {
    setLoading(true);
    try {
      const r = await api<{ unreadCount: number }>("account/notifications/read", { method: "POST", body: {} });
      setUnread(r.unreadCount || 0);
      setItems((prev) => prev.map((n) => ({ ...n, isRead: true })));
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function openItem(n: Notification) {
    setOpen(false);
    if (!n.isRead) {
      // Optimistic: mark it read locally, then persist.
      setItems((prev) => prev.map((x) => (x.notificationId === n.notificationId ? { ...x, isRead: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
      await api("account/notifications/read", { method: "POST", body: { notificationId: n.notificationId } })
        .catch(() => void load());
    }
    if (n.link) router.push(n.link);
  }

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label={unread > 0 ? `Notifications (${unread} unread)` : "Notifications"}
        className="relative rounded-md p-1.5 text-gray-500 transition hover:bg-gray-100 hover:text-gray-700"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} className="h-5 w-5">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
          />
        </svg>
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-danger-500 px-1 text-[10px] font-bold text-pure-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className={`absolute top-full z-50 mt-2 w-80 max-w-[calc(100vw-1.5rem)] animate-scale-in overflow-hidden rounded-xl bg-white shadow-dropdown ring-1 ring-black/5 ${
            align === "left" ? "left-0" : "right-0"
          }`}
        >
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5">
            <span className="text-sm font-semibold text-gray-900">Notifications</span>
            {unread > 0 && (
              <button
                onClick={markAllRead}
                disabled={loading}
                className="text-xs font-medium text-brand-600 hover:underline disabled:opacity-50"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-gray-500">Nothing yet. We&apos;ll tell you when something happens.</p>
            ) : (
              items.map((n) => (
                <button
                  key={n.notificationId}
                  onClick={() => openItem(n)}
                  className={`flex w-full gap-2.5 border-b border-gray-50 px-4 py-3 text-left transition hover:bg-gray-50 ${
                    n.isRead ? "" : "bg-brand-50/40 dark:bg-brand-500/10"
                  }`}
                >
                  <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${SEVERITY_DOT[n.severity] ?? SEVERITY_DOT.info}`} />
                  <span className="min-w-0 flex-1">
                    <span className={`block text-sm ${n.isRead ? "text-gray-700" : "font-semibold text-gray-900"}`}>
                      {n.title}
                    </span>
                    {n.body && <span className="mt-0.5 block text-xs text-gray-500">{n.body}</span>}
                    <span className="mt-1 block text-[11px] text-gray-400">{timeAgo(n.createdDate)}</span>
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
